import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import CreateOrgDialog from '../dialogs/CreateOrgDialog';
import { toast } from 'sonner';
import { API_URL } from '../../config';

const DashboardLayout = () => {
  const {
    user,
    isAuthenticated,
    loading,
    selectOrganization,
    currentOrg,
    superAdminViewMode,
  } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/organizations`);
      setOrganizations(response.data);
      
      // Auto-select first org if none selected
      if (!currentOrg && response.data.length > 0) {
        selectOrganization(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  }, [currentOrg, selectOrganization]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrganizations();
    }
  }, [isAuthenticated, fetchOrganizations]);

  // Super admin "admin view": only super-admin routes; keep them out of workspace URLs.
  useEffect(() => {
    if (loading || loadingOrgs) return;
    if (user?.platform_role !== 'super_admin') return;
    if (superAdminViewMode !== 'super_admin') return;
    if (!location.pathname.startsWith('/dashboard/super-admin')) {
      navigate('/dashboard/super-admin/overview', { replace: true });
    }
  }, [
    user?.platform_role,
    superAdminViewMode,
    location.pathname,
    loading,
    loadingOrgs,
    navigate,
  ]);

  const handleCreateOrg = async (name) => {
    try {
      const response = await axios.post(`${API_URL}/organizations`, { name });
      const newOrg = response.data;
      setOrganizations(prev => [...prev, newOrg]);
      selectOrganization(newOrg);
      setShowCreateOrg(false);
      toast.success('Workspace created successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create workspace';
      toast.error(message);
      throw error;
    }
  };

  if (loading || loadingOrgs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Sidebar
        organizations={organizations}
        onCreateOrg={() => setShowCreateOrg(true)}
      />
      
      {/* Main Content */}
      <main className="ml-64 min-h-screen" data-testid="dashboard-main">
        <Outlet context={{ organizations, fetchOrganizations }} />
      </main>

      {/* Create Organization Dialog */}
      <CreateOrgDialog
        open={showCreateOrg}
        onOpenChange={setShowCreateOrg}
        onSubmit={handleCreateOrg}
      />
    </div>
  );
};

export default DashboardLayout;
